import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBESSStore } from '../../app/store';
import { useServerStatus } from '../../hooks/useServerStatus';
import { LanguageToggle } from './LanguageToggle';

export function Header() {
  const { t } = useTranslation();
  const { resetAll } = useBESSStore();
  const { isConnected, isChecking } = useServerStatus();

  // Check if authentication is enabled
  const isAuthEnabled = import.meta.env.VITE_ENABLE_AUTH === 'true';

  // Only use auth hook if authentication is enabled
  let user = null;
  let logout = () => {};

  if (isAuthEnabled) {
    try {
      // Dynamic import to avoid errors when auth is disabled
      const { useAuth } = require('../../contexts/AuthContext');
      const authContext = useAuth();
      user = authContext.user;
      logout = authContext.logout;
    } catch (e) {
      // Auth context not available, user will be null
    }
  }

  return (
    <header 
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #4a5568 100%)',
        minHeight: '180px'
      }}
    >
      {/* Pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-8 py-16 text-center">
        {/* Logo, BESS text, and Icon */}
        <div className="flex items-center justify-center mb-6">
          <img 
            src="/logoterralink.png" 
            alt="Terralink" 
            className="h-12 brightness-0 invert"
          />
          <div className="flex items-center gap-1 ml-6">
            <h1
              className="text-white text-4xl font-black tracking-tight"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900 }}
            >
              BESS
            </h1>
            {/* White Battery Icon */}
            <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 32 32"
            className="w-12 h-12"
          >
            {/* Battery body - white outline */}
            <rect x="4" y="8" width="20" height="16" rx="2" fill="none" stroke="white" strokeWidth="2"/>
            
            {/* Battery terminal - white */}
            <rect x="24" y="13" width="4" height="6" rx="1" fill="white"/>
            
            {/* Battery charge segments */}
            <rect x="7" y="11" width="3" height="10" fill="white"/>
            <rect x="11.5" y="11" width="3" height="10" fill="white"/>
            <rect x="16" y="11" width="3" height="10" fill="white"/>
          </svg>
          </div>
        </div>
        
        {/* Subtitle */}
        <p className="text-lg text-[#e2e8f0] font-normal max-w-[700px] mx-auto">
          {t('header.title')}
        </p>
        
        {/* Top left - Portfolio link */}
        <div className="absolute top-4 left-4">
          <Link
            to="/portfolio"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary/80 hover:bg-brand-primary backdrop-blur-sm border border-white/20 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/>
            </svg>
            <span className="text-sm text-white font-medium">Portafolio</span>
          </Link>
        </div>

        {/* Top right section with user info and server status */}
        <div className="absolute top-4 right-4 flex items-center gap-3">
          {/* Language Toggle */}
          <LanguageToggle />

          {/* User Profile - only show if auth is enabled and user exists */}
          {isAuthEnabled && user && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-black/20 backdrop-blur-sm border border-white/10">
              <div className="text-right">
                <p className="text-sm text-white font-medium">{user.name}</p>
                <p className="text-xs text-white/70">{user.email}</p>
              </div>
              <button
                onClick={logout}
                className="px-3 py-1.5 text-sm text-white bg-white/10 hover:bg-white/20 rounded transition-colors border border-white/20"
                title="Volver al Portal"
              >
                Salir
              </button>
            </div>
          )}

          {/* Development Mode Indicator - show when auth is disabled */}
          {!isAuthEnabled && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500/20 backdrop-blur-sm border border-orange-400/30">
              <span className="text-sm text-orange-200 font-medium">
                🔓 Dev Mode (Auth Disabled)
              </span>
            </div>
          )}

          {/* Server Status Indicator */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/20 backdrop-blur-sm border border-white/10">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                isChecking ? 'bg-yellow-400 animate-pulse' :
                isConnected ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <span className="text-sm text-white/90">
                {isChecking ? t('header.checking') :
                 isConnected ? t('header.apiConnected') : t('header.apiDisconnected')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}